import {
  Alert,
  Avatar,
  Card,
  CardBody,
  DateTime,
  FilteredSelect,
  Form,
  FormField,
  GDSDate,
  Heading,
  Icon,
  InputGroup,
  NewDateInput,
  Paragraph,
  Selectable,
  SelectableItem,
  Separator,
  StructuredInfo,
  StructuredInfoItem,
  TextInput,
  useMatchMediaTierProp,
} from "@george-labs.com/design-system"
import { EcsaBranchDTO, FreeDay } from "@smartx/mikro-vt-client-api"
import { useFormik } from "formik"
import dayjs from "../../utils/dayjs"
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import * as Yup from "yup"
import { bookingApi } from "../../api/apiClient"
import { useAppDispatch, useBranches, useOfferData } from "../../app/hooks"
import { findBranches } from "../../app/slices/branchesSlice"
import {
  createDateVariable,
  formatDateString,
  parseBookingDateTime,
} from "../../functions/helpers"
import AuthContext from "../../providers/AuthProvider"
import { RequestHeader } from "../../utils/utils"
import {
  AppointmentRetryAlert,
  AppointmentTimeDuration,
} from "./OfferAppointmentHelperComponents"
import { OfferNavButtons, OfferSaveButton } from "./OfferNavButtons"
import OpeningData from "./OpeningData"
import { ICON } from "@george-labs.com/george-assets/icons"

export interface IAppointmentData {
  branch: string
  bookingStartTime: string
  bookingEndTime: string
  duration: number
  bookingCode: string
}

export interface IAppointmentFormValues {
  search: string
  branch: string[]
  date: string
  time: string[]
}

enum APP_STEPS_ENUM {
  Branch = "branch",
  Date = "date",
  Create = "create",
}

const APP_STEPS: APP_STEPS_ENUM[] = Object.values(APP_STEPS_ENUM)

const OfferAppointment = () => {
  const authContext = useContext(AuthContext)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const offerData = useOfferData()
  const branches = useBranches()
  const [step, setStep] = useState(APP_STEPS_ENUM.Branch)
  const [freeDays, setFreeDays] = useState<FreeDay[]>([])
  const [selectedDay, setSelectedDay] = useState<FreeDay>(null)
  const [hasError, setHasError] = useState(false)
  const [hasNoDates, setHasNoDates] = useState(false)

  const initialValues = useMemo(
    (): IAppointmentFormValues => ({
      search: "",
      branch: [],
      date: "",
      time: [],
    }),
    [],
  )

  useEffect(() => {
    if (!branches?.length) {
      authContext.refresh()
      dispatch(findBranches())
    }
  }, [authContext, branches, dispatch])

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      branch: Yup.array().min(1, t("offers.appointment.warning.branch")),
      date: Yup.string().test(
        "date-empty",
        t("offers.appointment.warning.date"),
        (value) => step !== APP_STEPS_ENUM.Date || !!value,
      ),
      time: Yup.array().test(
        "time-empty",
        t("offers.appointment.warning.time"),
        (value, context) => {
          const date = context?.parent?.date
          return step !== APP_STEPS_ENUM.Date || !date || !!value?.length
        },
      ),
    }),
    onSubmit: (values) => handleSubmit(values),
  })

  const handleSubmit = (_values: any) => {
    nextButtonHandler()
  }

  const nextButtonHandler = async () => {
    await formik.validateForm()

    if (!formik.isValid) {
      return
    }

    const nextStepIdx = APP_STEPS.indexOf(step) + 1

    switch (step) {
      case APP_STEPS_ENUM.Branch: {
        getFreeDaysByBranchId()
        break
      }

      case APP_STEPS_ENUM.Date: {
        createAppointment()
        break
      }
    }

    const nextStep = APP_STEPS[nextStepIdx]
    setStep(nextStep)
  }

  const handleDateChange = (val: GDSDate) => {
    formik.resetForm({
      values: formik.values,
      submitCount: 0,
    })

    const day = freeDays?.find(
      (el) =>
        !!el.date && dayjs(createDateVariable(el.date)).isSame(dayjs(val), "d"),
    )
    setSelectedDay(day)
    formik.setFieldValue("date", val?.toISOString())
  }

  const getFreeDaysByBranchId = async () => {
    const branchId = formik.values.branch?.[0]
    if (!branchId) {
      return
    }

    authContext.refresh()
    const result = await bookingApi.getFreeDays({
      header: new RequestHeader(),
      branchId,
    })

    const freeDays = result?.data?.freeDays || []
    setFreeDays(freeDays)

    if (!freeDays?.length) {
      setHasNoDates(true)
    }
  }

  const createAppointment = async () => {
    const branchId = formik.values.branch?.[0]
    const date = selectedDay?.date ? selectedDay.date : null
    const time = formik.values.time?.[0]

    if (!branchId || !date || !time) {
      return
    }

    const datePart = formatDateString(date)
    const bookingTime = `${datePart}T${time}:00`

    authContext.refresh()
    const result = await bookingApi.createAppointment({
      header: new RequestHeader(),
      bookingTime,
      branchId,
      caseId: offerData?.caseId,
    })

    if (result?.data?.success) {
      const branch = branches?.find((el) => el.id === branchId)
      const confirmedBookingTime = parseBookingDateTime(result.data.bookingTime)
      const bookedDateTimeParsed = dayjs(confirmedBookingTime)
      const bookingEndTime = bookedDateTimeParsed.isValid()
        ? bookedDateTimeParsed
            .add(selectedDay.interval, "minutes")
            .toISOString()
        : null
      const appointment: IAppointmentData = {
        branch: getBranchInfo(branch, true),
        bookingStartTime: confirmedBookingTime,
        bookingEndTime,
        duration: selectedDay.interval,
        bookingCode: result.data.bookingCode,
      }
      navigate("/offer/info/success", {
        state: { appointment },
        replace: true,
      })
      return
    }

    setHasError(true)
  }

  const getBranchInfo = useCallback(
    (branch: EcsaBranchDTO, addressOnly?: boolean, separator: string = "") => {
      return [
        addressOnly ? "" : branch?.description,
        separator,
        branch?.address?.zipCode,
        branch?.address?.city,
        branch?.address?.street,
      ]
        .filter((el) => !!el)
        ?.join(" ")
    },
    [],
  )

  const getResults = useCallback(() => {
    return branches?.filter((el) => {
      const searchIn = getBranchInfo(el)

      return searchIn
        ?.toUpperCase()
        ?.includes(formik.values.search?.toUpperCase())
    })
  }, [formik, branches, getBranchInfo])

  const isDateInvalid = useCallback(
    (date: GDSDate) => {
      return !freeDays.some((el) => {
        const dateToCheck = el?.date ? createDateVariable(el.date) : null

        return dateToCheck ? dayjs(dateToCheck).isSame(dayjs(date), "d") : false
      })
    },
    [freeDays],
  )

  const getFreeTimes = useCallback(() => {
    return selectedDay?.freeTimes || []
  }, [selectedDay])

  const handleRestart = () => {
    formik.resetForm({ values: initialValues })
    setStep(APP_STEPS_ENUM.Branch)
    setFreeDays([])
    setSelectedDay(null)
    setHasError(false)
    setHasNoDates(false)
  }

  const headerContent = (
    <Heading level={1}>{t("offers.appointment.title")}</Heading>
  )

  const textContent = (
    <>
      {step === APP_STEPS_ENUM.Branch && (
        <>
          <Paragraph style={{ marginBottom: "1rem" }}>
            {t("offers.appointment.text")}
          </Paragraph>
          <Paragraph style={{ marginBottom: "1rem" }}>
            {t("offers.appointment.text2")}
          </Paragraph>
        </>
      )}

      {step === APP_STEPS_ENUM.Date && (
        <Paragraph style={{ marginBottom: "1rem" }}>
          {t("offers.appointment.text3", {
            days: offerData?.palContractSignatureTimeoutInDays,
          })}
        </Paragraph>
      )}
    </>
  )

  const searchContent = useMatchMediaTierProp({
    xs: (
      <InputGroup prepend={<Icon assetIcon={ICON.SEARCH} />}>
        <FilteredSelect
          items={branches?.map((el) => ({
            label: getBranchInfo(el, false, " - "),
            value: el.id,
          }))}
          value={formik.values.branch?.[0]}
          onChange={formik.handleChange}
          placeholder={t("offers.appointment.search")}
          onSelect={(val: any) => formik.setFieldValue("branch", [val?.value])}
          emptyLabel={t("offers.appointment.noresult")}
        />
      </InputGroup>
    ),
    md: (
      <>
        <InputGroup prepend={<Icon assetIcon={ICON.SEARCH} />}>
          <TextInput
            value={formik.values.search}
            placeholder={t("offers.appointment.search")}
            onChange={formik.handleChange}
            name="search"
            autoComplete="off"
          />
        </InputGroup>

        {formik.values.search && (
          <>
            <Heading level={3} style={{ marginTop: "1rem" }}>
              {t("offers.appointment.results")}
            </Heading>

            <Selectable
              type={Selectable.TYPE.RADIO}
              verticalAlign={Selectable.VERTICAL_ALIGN.MIDDLE}
              selectOnClick={true}
              selectedItems={formik.values.branch}
              onSelect={(selected) => formik.setFieldValue("branch", selected)}
            >
              {getResults()?.map((branch) => (
                <Fragment key={`branch-wrapper-${branch.id}`}>
                  <Card key={`branch-${branch.id}`} className="mb-2">
                    <SelectableItem value={branch.id}>
                      <StructuredInfo
                        key={branch.id}
                        avatar={<Avatar icon={Avatar.ICON.USER} />}
                        header={
                          <>
                            <StructuredInfoItem>
                              {branch.description}
                            </StructuredInfoItem>
                            <StructuredInfoItem
                              size={StructuredInfoItem.SIZE.SMALL}
                              faded
                            >
                              {getBranchInfo(branch, true)}
                            </StructuredInfoItem>
                          </>
                        }
                      />
                    </SelectableItem>
                  </Card>

                  {formik.values.branch?.[0] === branch.id && (
                    <OpeningData branch={branch} />
                  )}
                </Fragment>
              ))}
            </Selectable>
          </>
        )}
      </>
    ),
  })

  const appointmentContent = (
    <>
      <FormField
        input={
          <NewDateInput
            placeholder={t("offers.appointment.datePlaceholder")}
            name="date"
            displayFormat="yyyy.MM.dd."
            onDateChange={(val) => handleDateChange(val)}
            value={formik.values.date}
            autoCapitalize="sentences"
            isOutsideRange={isDateInvalid}
          />
        }
        label={t("offers.appointment.date")}
        optionalLabel=""
        labelPosition={FormField.LABEL_POSITION.ABOVE}
      />

      {formik.values.date && (
        <>
          <Heading level={3} style={{ marginTop: "1rem" }}>
            <DateTime
              date={new Date(formik.values.date)}
              format="MMMM dd. EEEE"
            />
          </Heading>

          <Card nested>
            <Selectable
              type={Selectable.TYPE.RADIO}
              verticalAlign={Selectable.VERTICAL_ALIGN.MIDDLE}
              selectOnClick={true}
              selectedItems={formik.values.time}
              onSelect={(selected) => formik.setFieldValue("time", selected)}
            >
              {getFreeTimes()?.map((time) => (
                <>
                  <Card key={`time-${time.time}`}>
                    <SelectableItem value={time.time}>
                      <StructuredInfo
                        key={time.time}
                        header={
                          <StructuredInfoItem>
                            <AppointmentTimeDuration
                              startTime={time.time}
                              duration={selectedDay.interval}
                            />
                          </StructuredInfoItem>
                        }
                      />
                    </SelectableItem>
                  </Card>
                    <Separator />
                </>
              ))}
            </Selectable>
          </Card>
        </>
      )}
    </>
  )

  const content = (
    <>
      {step === APP_STEPS_ENUM.Branch && searchContent}

      {step === APP_STEPS_ENUM.Date && appointmentContent}
    </>
  )

  const navButtonsContent =
    step === APP_STEPS_ENUM.Date ? (
      <OfferNavButtons onPrevButtonClicked={handleRestart} />
    ) : (
      <OfferNavButtons />
    )

  const saveButtonHandler = () => {
    navigate("/dashboard", { replace: true })
  }

  const saveButtonContent = (
    <OfferSaveButton onSaveButtonClicked={saveButtonHandler} />
  )

  const warning = (
    <>
      {!formik.isValid && !!formik.submitCount && (
        <div>
          <Alert
            heading=""
            variant={Alert.VARIANT.WARNING}
            hidden={step !== APP_STEPS_ENUM.Branch}
          >
            {formik.errors.branch?.toString()}
          </Alert>

          <Alert
            heading=""
            variant={Alert.VARIANT.WARNING}
            hidden={step !== APP_STEPS_ENUM.Date || !!formik.values.date}
          >
            {formik.errors.date?.toString()}
          </Alert>

          <Alert
            heading=""
            variant={Alert.VARIANT.WARNING}
            hidden={step !== APP_STEPS_ENUM.Date || !formik.values.date}
          >
            {formik.errors.time?.toString()}
          </Alert>
        </div>
      )}

      {hasError && (
        <AppointmentRetryAlert
          retryButtonClicked={handleRestart}
          text="offers.appointment.errorText"
        />
      )}

      {hasNoDates && (
        <AppointmentRetryAlert
          retryButtonClicked={handleRestart}
          text="offers.appointment.noDatesText"
        />
      )}
    </>
  )

  const pageContent = useMatchMediaTierProp({
    xs: (
      <>
        {headerContent}

        {textContent}

        {content}

        {warning}

        {navButtonsContent}
      </>
    ),
    md: (
      <Card nested={false}>
        <CardBody>
          {headerContent}

          {textContent}

          {content}

          {warning}
        </CardBody>

        <Separator />

        <CardBody>{navButtonsContent}</CardBody>
      </Card>
    ),
  })

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault()
        formik.handleSubmit()
      }}
    >
      {pageContent}

      {saveButtonContent}
    </Form>
  )
}

export default OfferAppointment
